import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const MAX_POLLS = 10;

export default function SubscriptionSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ phase: "polling", data: null });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus({ phase: "error", data: { message: "session_id manquant" } });
      return;
    }
    let cancelled = false;
    const poll = async (n = 0, consecutiveErrors = 0) => {
      if (cancelled) return;
      if (n >= MAX_POLLS) {
        setStatus({ phase: "timeout", data: null });
        return;
      }
      try {
        const s = await api.checkoutStatus(sessionId);
        if (s.payment_status === "paid") {
          setStatus({ phase: "success", data: s });
          return;
        }
        if (s.status === "expired") {
          setStatus({ phase: "expired", data: s });
          return;
        }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1, 0), 2000);
      } catch (e) {
        // Retry transient errors within the poll budget
        if (consecutiveErrors < 3 && n < MAX_POLLS - 1) {
          setAttempts(n + 1);
          setTimeout(() => poll(n + 1, consecutiveErrors + 1), 2500);
        } else {
          setStatus({ phase: "error", data: { message: e.response?.data?.detail || e.message } });
        }
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-bg text-white flex items-center justify-center p-6" data-testid="subscription-success-page">
      <div className="panel p-8 max-w-md w-full text-center">
        {status.phase === "polling" && (
          <>
            <Loader2 size={48} className="text-accent-blue mx-auto animate-spin mb-4" />
            <h1 className="font-heading text-2xl mb-2">Confirmation du paiement…</h1>
            <p className="text-white/60 text-sm">Tentative {attempts + 1} / {MAX_POLLS}</p>
          </>
        )}
        {status.phase === "success" && (
          <>
            <CheckCircle2 size={64} className="text-up mx-auto mb-4" />
            <h1 className="font-heading text-3xl mb-3">Paiement confirmé !</h1>
            <p className="text-white/70 mb-6" data-testid="subscription-success-message">
              Bienvenue dans le tier <b>{status.data?.tier_id}</b>. Ton compte est activé.
            </p>
            <Link to="/app" className="btn btn-primary w-full justify-center" data-testid="subscription-goto-app">
              Aller au Dashboard
            </Link>
          </>
        )}
        {status.phase === "expired" && (
          <>
            <AlertCircle size={48} className="text-warn mx-auto mb-4" />
            <h1 className="font-heading text-2xl mb-2">Session expirée</h1>
            <Link to="/pricing" className="btn btn-primary mt-4">Retour aux tarifs</Link>
          </>
        )}
        {status.phase === "timeout" && (
          <>
            <AlertCircle size={48} className="text-warn mx-auto mb-4" />
            <h1 className="font-heading text-2xl mb-2">Vérification en cours</h1>
            <p className="text-white/60 text-sm mb-4">Le paiement peut prendre quelques minutes à être confirmé. Vérifie ton email.</p>
            <Link to="/app" className="btn">Aller au Dashboard</Link>
          </>
        )}
        {status.phase === "error" && (
          <>
            <AlertCircle size={48} className="text-down mx-auto mb-4" />
            <h1 className="font-heading text-2xl mb-2">Erreur</h1>
            <p className="text-white/60 text-sm mb-4">{status.data?.message}</p>
            <Link to="/pricing" className="btn">Retour aux tarifs</Link>
          </>
        )}
      </div>
    </div>
  );
}
